import React, { ReactNode, useState } from 'react';
import { Tabs, Layout, Drawer, Button } from 'antd';
import { MenuUnfoldOutlined } from '@ant-design/icons';

import MessagesListItem, { Channel } from '../MessagesListItem';
import { useWindowSize } from '../../hooks/useWindowSize';
import IconCloseOutlined from '../Icons/IconCloseOutlined';
import MessengerBody, {
  MessengerBodyPropsType,
} from './components/MessengerBody';
import './Chat.less';

export type ChannelListProps = {
  channels?: Channel[];
  height?: string | number;
  isFetching?: Boolean;
  header?: ReactNode;
  onChangePage?: (condition?: any) => void;
  onChannelChange?: (condition?: any) => void;
  activeChannel?: string;
  onItemClick?: (
    key: string,
    event: React.MouseEvent<HTMLInputElement>,
  ) => void;
  messenger: MessengerBodyPropsType;
  onScrollBottom?: (channels: Channel[]) => Promise<any>;
  disableOnScroll?: boolean;
};

const TabPane = Tabs.TabPane;
const { Content } = Layout;

const TChannelList: React.FC<ChannelListProps> = ({
  channels = [],
  height,
  onChannelChange,
  activeChannel,
  onItemClick,
  header,
  messenger,
  onScrollBottom,
  disableOnScroll = false,
}) => {
  const [loading, setLoading] = useState(false);

  const [drawerVisible, setDrawerVisible] = useState(false);
  const openDrawer = () => setDrawerVisible(true);
  const closeDrawer = () => setDrawerVisible(false);

  const handleChannelClick = (
    channelId: string,
    event: React.MouseEvent<HTMLInputElement>,
  ) => {
    if (typeof onItemClick === 'function') {
      onItemClick(channelId, event);
    }
    closeDrawer();
  };

  const handleOnScrollTop = () => {
    if (typeof onScrollBottom === 'function' && !loading) {
      setLoading(true);
      onScrollBottom(channels).then(() => {
        setLoading(false);
      });
    }
  };

  const getScrollPosition = (e) => {
    if (disableOnScroll) {
      return;
    }
    const y =
      e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight;
    if (y === 0) {
      handleOnScrollTop();
    }
  };

  const size = useWindowSize();
  const mobile = size.width < 769;

  if (!mobile) {
    return (
      <div className="t-chat-container">
        <Tabs
          className="t-chat"
          tabPosition="left"
          tabBarGutter={0}
          activeKey={activeChannel}
          onChange={onChannelChange}
          onTabClick={onItemClick}
          tabBarExtraContent={header}
          tabBarStyle={{
            marginBottom: mobile ? '50px' : 'auto',
            height: height,
            width: mobile ? '100%' : '25%',
          }}
          onScroll={getScrollPosition}
          destroyInactiveTabPane={true}
        >
          {channels.map((item) => (
            <TabPane
              tab={<MessagesListItem channel={item} />}
              key={item.id}
              style={{ paddingLeft: !mobile ? '16px' : '0px' }}
            >
              <MessengerBody
                {...messenger}
                channel={item}
                channels={channels}
                filter={header}
              />
            </TabPane>
          ))}
        </Tabs>
      </div>
    );
  }

  const channel = channels.find((channel) => channel.id === activeChannel);

  return (
    <Layout className="t-chat-mobile-layout">
      <Drawer
        headerStyle={{ padding: 0 }}
        className="chat-drawer"
        closeIcon={null}
        placement="left"
        onClose={closeDrawer}
        visible={drawerVisible}
        bodyStyle={{ padding: 0 }}
        width={240}
      >
        <div className="drawer-close">
          <IconCloseOutlined
            size="md"
            fill="currentColor"
            onClick={closeDrawer}
          />
        </div>

        <div onScroll={getScrollPosition} className="drawer-container">
          {!!header && <div className="drawer-header">{header}</div>}
          {channels.map((item) => (
            <div
              key={item.id}
              className={`drawer-channel-item ${
                item.id === activeChannel ? 'drawer-channel-item-active' : ''
              }`}
              onClick={(e: any) => handleChannelClick(item.id, e)}
            >
              <MessagesListItem channel={item} />
            </div>
          ))}
        </div>
      </Drawer>

      <Button
        size="large"
        type="primary"
        className="drawer-button"
        icon={<MenuUnfoldOutlined />}
        onClick={openDrawer}
      />

      <Content style={{ overflow: 'auto' }}>
        {!!channel && (
          <MessengerBody
            {...messenger}
            channel={channel}
            channels={channels}
            filter={header}
          />
        )}
      </Content>
    </Layout>
  );
};

export default TChannelList;
